import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, api, isMobileApp } from "../api";
import { clearMobileToken, readMobileToken, writeMobileToken } from "../native/secure-store";
import { clearStandCache } from "../modules/stand/cache";
import type { SessionUser } from "../../shared/types";

const MOBILE_IDENTITY_KEY = "mb-music-stand-identity-v1";

interface MobileIdentity { user: SessionUser; expiresAt: string }
interface SessionValue {
  user: SessionUser | null;
  loading: boolean;
  offlineSession: boolean;
  refresh: () => Promise<void>;
  loginMobile: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

function readMobileIdentity(): MobileIdentity | null {
  try {
    const raw = localStorage.getItem(MOBILE_IDENTITY_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as MobileIdentity;
    if (!value.user?.id || !value.user?.email || !value.expiresAt || Date.parse(value.expiresAt) <= Date.now()) return null;
    return value;
  } catch { return null; }
}

function saveMobileIdentity(identity: MobileIdentity): void {
  localStorage.setItem(MOBILE_IDENTITY_KEY, JSON.stringify(identity));
}

function clearMobileIdentity(): void {
  localStorage.removeItem(MOBILE_IDENTITY_KEY);
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [offlineSession, setOfflineSession] = useState(false);

  async function refresh() {
    try {
      if (isMobileApp()) {
        const token = await readMobileToken();
        if (!token) {
          clearMobileIdentity();
          await clearStandCache().catch(() => undefined);
          setUser(null);
          setOfflineSession(false);
          return;
        }
      }
      const controller = new AbortController();
      const timeout = isMobileApp() ? window.setTimeout(() => controller.abort(), 8000) : 0;
      try {
        const active = await api<SessionUser>("/api/v1/auth/session", { signal: controller.signal });
        setUser(active);
        setOfflineSession(false);
        if (isMobileApp()) {
          const saved = readMobileIdentity();
          if (saved) saveMobileIdentity({ user: active, expiresAt: saved.expiresAt });
        }
      } finally {
        if (timeout) window.clearTimeout(timeout);
      }
    } catch (caught) {
      if (isMobileApp() && !(caught instanceof ApiError)) {
        const saved = readMobileIdentity();
        const token = await readMobileToken().catch(() => null);
        if (saved && token) {
          setUser(saved.user);
          setOfflineSession(true);
          return;
        }
      }
      if (isMobileApp() && (caught instanceof ApiError && caught.status === 401 || !(await readMobileToken().catch(() => null)))) {
        await clearMobileToken().catch(() => undefined);
        clearMobileIdentity();
        await clearStandCache().catch(() => undefined);
      }
      setUser(null);
      setOfflineSession(false);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    void refresh();
    const onLost = () => {
      setUser(null);
      setOfflineSession(false);
      if (isMobileApp()) {
        void clearMobileToken().catch(() => undefined);
        clearMobileIdentity();
        void clearStandCache().catch(() => undefined);
      }
    };
    window.addEventListener("mb-unauthorized", onLost);
    return () => window.removeEventListener("mb-unauthorized", onLost);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.density = user?.preferences.density ?? "comfortable";
    document.documentElement.dataset.contrast = user?.preferences.contrast ?? "default";
  }, [user]);

  const value = useMemo<SessionValue>(() => ({
    user, loading, offlineSession, refresh,
    loginMobile: async (email, password) => {
      const result = await api<{ token: string; user: SessionUser; expiresAt: string }>("/api/v1/auth/mobile-login", {
        method: "POST", body: JSON.stringify({ email, password }),
      });
      await writeMobileToken(result.token);
      saveMobileIdentity({ user: result.user, expiresAt: result.expiresAt });
      setUser(result.user);
      setOfflineSession(false);
      setLoading(false);
    },
    signOut: async () => {
      const controller = new AbortController();
      const timeout = isMobileApp() ? window.setTimeout(() => controller.abort(), 4000) : 0;
      try {
        await api(isMobileApp() ? "/api/v1/auth/mobile-logout" : "/api/v1/auth/logout", { method: "POST", signal: controller.signal });
      } catch {
        // Remove private local content even when the server cannot be reached.
      } finally {
        if (timeout) window.clearTimeout(timeout);
        if (isMobileApp()) await clearMobileToken().catch(() => undefined);
        clearMobileIdentity();
        await clearStandCache().catch(() => undefined);
        setUser(null);
        setOfflineSession(false);
      }
    },
  }), [user, loading, offlineSession]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("Session missing");
  return value;
}
